import { Component, inject, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SnackbarService } from '../../../services/design/snackbar.service';
import {
  UserFormModel,
  UsersService,
} from '../../../services/users/users.service';
import {
  StoreService,
  StoreOption,
} from '../../../services/stores/store.service';
import { AuthService } from '../../../services/auth/auth.service';
import { ADMIN_PANEL_ROLES, assignedStaffRoles, isAdminPanelRole, roleLabel } from '../../../core/auth/roles';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './userDetail.component.html',
})
export class UserDetail implements OnInit {
  private userService = inject(UsersService);
  private storeService = inject(StoreService);
  private snackbar = inject(SnackbarService);
  private auth = inject(AuthService);

  closeWithResult!: (result: UserFormModel | null) => void;
  @Input() user!: UserFormModel;

  stores: StoreOption[] = [];
  saving = false;
  canChangeAccess = false;
  readonly availableRoles=ADMIN_PANEL_ROLES;
  readonly roleLabel=roleLabel;
  get editingStaff() { return this.user.isEdit && isAdminPanelRole(this.user.role); }
  toggleRole(role: string, checked: boolean) {
    const roles=this.user.staffRoles ?? [];
    this.user.staffRoles=checked && role==='superadmin' ? ['superadmin']
      : checked ? [...new Set([...roles.filter(r=>r!=='superadmin'),role])] : roles.filter(r=>r!==role);
  }

  get isSuperAdmin() {
    return this.auth.isSuperAdmin();
  }

  ngOnInit() {
    this.user.staffRoles ??= assignedStaffRoles(this.user.role,this.user.additional_roles);
    this.user.originalStaffRoles ??= [...this.user.staffRoles];
    this.canChangeAccess = !this.user.isEdit || (isAdminPanelRole(this.user.role) && this.user.id !== this.auth.user()?.id);
    this.storeService.allStores().subscribe({
      next: (stores) => {
        this.stores = stores;
      },
      error: () => {
        this.snackbar.error('Failed to load stores');
      },
    });
  }

  needsEmail(): boolean {
    return isAdminPanelRole(this.user.role);
  }

  needsPhone(): boolean {
    return this.user.role === 'rider' || this.user.role === 'user';
  }

  needsStore(): boolean {
    if(this.editingStaff)return !this.user.staffRoles?.includes('superadmin');
    return this.needsEmail() && this.user.role !== 'superadmin';
  }

  needsPassword(): boolean {
    return !this.user.isEdit && this.needsEmail();
  }

  async save() {
    if(this.saving)return;
    if(this.editingStaff && !this.user.staffRoles?.length){
      this.snackbar.error('Select at least one staff role. Use Disable to revoke access.');return;
    }
    if (!this.user.name?.trim()) {
      this.snackbar.error('Name is required');
      return;
    }

    if (!this.user.isEdit) {
      if (this.needsEmail() && !this.user.email) {
        this.snackbar.error('Email is required for admin accounts');
        return;
      }
      if (this.needsPhone() && !this.user.phone) {
        this.snackbar.error('Phone is required');
        return;
      }
      if (this.needsPassword() && !this.user.password) {
        this.snackbar.error('Temporary password is required');
        return;
      }
    }

    if (this.needsStore() && !this.user.storeId) {
      this.snackbar.error('Assign a store for this staff account');
      return;
    }

    this.saving = true;
    try {
      if (this.user.isEdit) {
        await this.userService.updateUser(this.user);
        this.snackbar.success('Account updated');
      } else {
        await this.userService.createUser(this.user);
        this.snackbar.success('Account created');
      }
      this.closeWithResult(this.user);
    } catch (err: any) {
      this.snackbar.error(err.message || 'Operation failed');
    } finally {
      this.saving = false;
    }
  }

  close() {
    this.closeWithResult(null);
  }
}
